/**
 * Customer Details Update Action
 * Triggered by: arc:action:customerDetailsUpdate
 */

import shopifyHelper from "../../js/shopify";
import { hasher } from "../../js/alpine/stores/hasher";
import UPDATE_CUSTOMER_DETAILS from '../../js/graphql/2024_01/customer/update.graphql';

export const runCustomerDetailsUpdate = async (data = {}) => {
    try {
        const { 
            customerId,
            firstName, 
            lastName, 
            email,
            phone = '',
            birthday = '',
            tags = [],
            acceptsMarketing = null,
            form,
            useAuth = true
        } = data;

        if (!customerId) {
            throw new Error('Customer ID is required');
        }

        if (!firstName || !lastName) {
            throw new Error('First name and last name are required');
        }

        // Email validation if provided
        if (email) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email)) {
                throw new Error('Please enter a valid email address');
            }
        }

        // Prepare tags array
        let updatedTags = [...tags];
        
        // Handle birthday tag
        if (birthday) {
            // Remove existing birthday tag
            updatedTags = updatedTags.filter(tag => !tag.startsWith('birthday:'));
            // Add new birthday tag
            updatedTags.push(`birthday:${birthday}`);
        }

        // Dispatch update attempt event
        window.dispatchEvent(new CustomEvent('customer:detailsUpdateAttempt', {
            detail: {
                customerId,
                firstName,
                lastName,
                email,
                phone,
                birthday,
                tags: updatedTags
            }
        }));

        // Prepare GraphQL request
        await hasher.store().dispatchHash();
        
        const variables = {
            input: {
                id: `gid://shopify/Customer/${customerId}`,
                firstName: firstName,
                lastName: lastName,
                tags: updatedTags
            }
        };

        // Add optional fields
        if (email) variables.input.email = email;
        if (phone) variables.input.phone = phone;
        if (acceptsMarketing !== null) variables.input.acceptsMarketing = acceptsMarketing;

        const body = JSON.stringify({
            query: UPDATE_CUSTOMER_DETAILS.loc.source.body,
            variables: variables
        });

        const data_obj = {
            options: {
                method: 'POST',
                headers: {
                    'X-Shopify-Access-Token': '<SHOPIFY_API_AUTHENTICATED_KEY>',
                    'Content-Type': 'application/json',
                    'X-MindArc-Hash': hasher.store().getHash()
                },
                body: body,
                request_body: UPDATE_CUSTOMER_DETAILS.loc.source.body,
            }
        };

        let jsonResponse = null;
        if (useAuth) {
            console.log('Customer Details Update: Using Authenticated Request');
            jsonResponse = await shopifyHelper.authenticatedFetch(data_obj);
        } else {
            jsonResponse = await shopifyHelper.unauthenticatedFetch(body);
        }

        console.log('Customer Details Update Response:', jsonResponse);

        if (jsonResponse.data?.customerUpdate) {
            const { customer, userErrors } = jsonResponse.data.customerUpdate;
            
            if (userErrors && userErrors.length > 0) {
                const errorMessages = userErrors.map(error => error.message).join(', ');
                throw new Error(errorMessages);
            }

            // Success
            window.dispatchEvent(new CustomEvent('customer:detailsUpdateSuccess', {
                detail: {
                    customer,
                    updatedFields: {
                        firstName,
                        lastName,
                        email,
                        phone,
                        birthday,
                        tags: updatedTags
                    },
                    timestamp: Date.now()
                }
            }));

            return {
                success: true,
                customer,
                message: 'Customer details updated successfully'
            };
        } else {
            throw new Error('Failed to update customer details');
        }

    } catch (error) {
        console.error('Customer Details Update error:', error);
        
        window.dispatchEvent(new CustomEvent('customer:detailsUpdateError', {
            detail: {
                error: error.message,
                customerId: data.customerId
            }
        }));

        return {
            success: false,
            error: error.message
        };
    }
};

// Initialize customer details form listeners
export const initializeCustomerDetailsUpdate = (config = {}) => {
    const {
        formSelector = '[data-customer-details-form]',
        autoSetupForms = true,
        trackFormChanges = true
    } = config;

    if (autoSetupForms) {
        setupDetailsUpdateForms(formSelector, trackFormChanges);
    }

    window.dispatchEvent(new CustomEvent('customerDetailsUpdate:initialized', {
        detail: { config, timestamp: Date.now() }
    }));
};

// Setup customer details form event listeners
const setupDetailsUpdateForms = (formSelector, trackFormChanges) => {
    const forms = document.querySelectorAll(formSelector);
    
    forms.forEach(form => {
        form.removeEventListener('submit', handleDetailsUpdateSubmit);
        form.addEventListener('submit', handleDetailsUpdateSubmit);

        if (trackFormChanges) {
            // Track form changes
            const inputs = form.querySelectorAll('input, select, textarea');
            inputs.forEach(input => {
                input.removeEventListener('input', handleFormInputChange);
                input.addEventListener('input', handleFormInputChange);
            });

            // Store original form state
            storeOriginalFormState(form);
        }
    });
};

// Handle details update form submissions
const handleDetailsUpdateSubmit = async (event) => {
    event.preventDefault();
    
    const form = event.target;
    const formData = new FormData(form);
    
    const customerId = formData.get('id') || form.dataset.customerId;
    const firstName = formData.get('first_name');
    const lastName = formData.get('last_name');
    const email = formData.get('email');
    const phone = formData.get('phone');
    const birthday = formData.get('birthday');
    const acceptsMarketing = formData.get('accepts_marketing') === '1';
    
    // Get existing tags and process them
    const tagsString = formData.get('tags') || '';
    const tags = tagsString.split(',').map(tag => tag.trim()).filter(tag => tag);

    // Disable submit button
    const submitBtn = form.querySelector('[type="submit"]');
    const originalText = submitBtn?.textContent;
    if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Updating...';
    }

    // Add loading state to form
    form.classList.add('processing');

    try {
        const result = await window.dispatchEvent(new CustomEvent('arc:action:customerDetailsUpdate', {
            detail: {
                customerId,
                firstName,
                lastName,
                email,
                phone,
                birthday,
                tags,
                acceptsMarketing,
                form
            }
        }));

        // Mark form as unchanged after successful update
        if (result?.success) {
            markFormAsUnchanged(form);
        }

    } finally {
        // Re-enable submit button and remove loading state
        if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = originalText;
        }
        form.classList.remove('processing');
    }
};

// Handle form input changes
const handleFormInputChange = (event) => {
    const form = event.target.closest('form');
    const hasChanged = checkFormHasChanged(form);
    
    form.dataset.hasChanged = hasChanged;
    
    window.dispatchEvent(new CustomEvent('customer:formChanged', {
        detail: {
            form,
            hasChanged,
            field: event.target.name,
            value: event.target.value
        }
    }));
};

// Store original form state
const storeOriginalFormState = (form) => {
    const formData = new FormData(form);
    const originalData = {};
    
    for (let [key, value] of formData.entries()) {
        originalData[key] = value;
    }
    
    form._originalData = originalData;
};

// Check if form has changed
const checkFormHasChanged = (form) => {
    if (!form._originalData) return false;
    
    const currentData = new FormData(form);
    
    for (let [key, value] of currentData.entries()) {
        if (form._originalData[key] !== value) {
            return true;
        }
    }
    
    return false;
};

// Mark form as unchanged (after successful save)
const markFormAsUnchanged = (form) => {
    storeOriginalFormState(form);
    form.dataset.hasChanged = 'false';
    
    window.dispatchEvent(new CustomEvent('customer:formSaved', {
        detail: { form }
    }));
};

// Get current form change status
export const getFormChangeStatus = (form) => {
    return {
        hasChanged: form.dataset.hasChanged === 'true',
        originalData: form._originalData || {}
    };
};

export default runCustomerDetailsUpdate;
